import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Paper,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import GroupsOutlinedIcon from '@mui/icons-material/GroupsOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import CloseIcon from '@mui/icons-material/Close';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { ThemeContext } from '../../components/ThemeContext/ThemeContext';
import http from '../../services/http';

const emptyForm = { id: null, name: '', description: '', codes: [] };

// Aceita registros colados separados por vírgula, ponto-e-vírgula, espaço ou quebra de linha.
const parseCodes = (raw) =>
  String(raw || '')
    .split(/[\s,;]+/)
    .map((c) => c.trim())
    .filter(Boolean);

const ClientGroupsPage = () => {
  const { theme } = useContext(ThemeContext);
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [codeInput, setCodeInput] = useState('');
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');
  // Expansão "conferir registros vinculados" (item 12 parte 2).
  const [expandedGroupId, setExpandedGroupId] = useState(null);
  const [detailsById, setDetailsById] = useState({}); // { [groupId]: { loading, companies:{code:name}, error } }

  const toggleExpand = async (group) => {
    if (expandedGroupId === group.id) {
      setExpandedGroupId(null);
      return;
    }
    setExpandedGroupId(group.id);
    if (detailsById[group.id]) return; // já carregado
    setDetailsById((prev) => ({ ...prev, [group.id]: { loading: true, companies: {} } }));
    try {
      const { data } = await http.get(`/client-groups/${group.id}`);
      const map = {};
      (Array.isArray(data?.companies) ? data.companies : []).forEach((c) => {
        if (c.code != null) map[String(c.code)] = c.name;
      });
      setDetailsById((prev) => ({ ...prev, [group.id]: { loading: false, companies: map } }));
    } catch (e) {
      setDetailsById((prev) => ({ ...prev, [group.id]: { loading: false, companies: {}, error: true } }));
    }
  };

  const isDark = theme === 'dark';

  const fetchGroups = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const { data } = await http.get('/client-groups');
      setGroups(Array.isArray(data) ? data : []);
    } catch (e) {
      setError('Não foi possível carregar os grupos de clientes.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchGroups();
  }, [fetchGroups]);

  const openCreate = () => {
    setForm(emptyForm);
    setCodeInput('');
    setSaveError('');
    setDialogOpen(true);
  };

  const openEdit = (group) => {
    setForm({
      id: group.id,
      name: group.name || '',
      description: group.description || '',
      codes: Array.isArray(group.codes) ? group.codes : [],
    });
    setCodeInput('');
    setSaveError('');
    setDialogOpen(true);
  };

  const closeDialog = () => {
    if (saving) return;
    setDialogOpen(false);
  };

  const addCodesFromInput = () => {
    const parsed = parseCodes(codeInput);
    if (!parsed.length) return;
    setForm((prev) => ({
      ...prev,
      codes: Array.from(new Set([...prev.codes, ...parsed])),
    }));
    setCodeInput('');
  };

  const removeCode = (code) => {
    setForm((prev) => ({ ...prev, codes: prev.codes.filter((c) => c !== code) }));
  };

  const handleCodeKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ',' || e.key === ';') {
      e.preventDefault();
      addCodesFromInput();
    }
  };

  const handleSave = async () => {
    if (!form.name.trim()) {
      setSaveError('Informe o nome do grupo.');
      return;
    }
    // Garante que registros digitados e não confirmados também entrem.
    const pending = parseCodes(codeInput);
    const codes = Array.from(new Set([...form.codes, ...pending]));

    setSaving(true);
    setSaveError('');
    const payload = {
      name: form.name.trim(),
      description: form.description.trim() || null,
      codes,
    };
    try {
      if (form.id) {
        await http.put(`/client-groups/${form.id}`, payload);
      } else {
        await http.post('/client-groups', payload);
      }
      setDialogOpen(false);
      fetchGroups();
    } catch (e) {
      setSaveError('Não foi possível salvar o grupo. Verifique os dados e tente novamente.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (group) => {
    if (!window.confirm(`Excluir o grupo "${group.name}"?`)) return;
    try {
      await http.delete(`/client-groups/${group.id}`);
      fetchGroups();
    } catch (e) {
      setError('Não foi possível excluir o grupo.');
    }
  };

  const dialogCodes = useMemo(() => form.codes, [form.codes]);

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 960, mx: 'auto' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, flexWrap: 'wrap', gap: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <GroupsOutlinedIcon color="primary" />
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>Grupos de clientes</Typography>
            <Typography variant="body2" color="text.secondary">
              Agrupe clientes por registro (código). Toda empresa com um dos registros informados é vinculada automaticamente ao grupo.
            </Typography>
          </Box>
        </Box>
        <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate}>
          Novo grupo
        </Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      ) : groups.length === 0 ? (
        <Paper variant="outlined" sx={{ p: 4, textAlign: 'center' }}>
          <GroupsOutlinedIcon sx={{ fontSize: 40, color: 'text.disabled', mb: 1 }} />
          <Typography variant="body1" sx={{ fontWeight: 500 }}>Nenhum grupo cadastrado</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Crie um grupo (ex.: "Lojas Online") e informe os registros dos clientes.
          </Typography>
          <Button variant="outlined" startIcon={<AddIcon />} onClick={openCreate}>Criar primeiro grupo</Button>
        </Paper>
      ) : (
        <List sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, p: 0 }}>
          {groups.map((group) => (
            <Paper key={group.id} variant="outlined" sx={{ p: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1 }}>
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>{group.name}</Typography>
                  {group.description && (
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                      {group.description}
                    </Typography>
                  )}
                  <Typography variant="caption" color="text.secondary">
                    {(group.codes?.length || 0)} registro(s) • {group.companies_count} empresa(s) vinculada(s)
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: 0.5, flexShrink: 0 }}>
                  <Tooltip title="Editar">
                    <IconButton size="small" onClick={() => openEdit(group)}>
                      <EditOutlinedIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Excluir">
                    <IconButton size="small" color="error" onClick={() => handleDelete(group)}>
                      <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
              </Box>
              {group.codes?.length > 0 && (
                <>
                  <Divider sx={{ my: 1 }} />
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {group.codes.slice(0, 30).map((code) => (
                      <Chip key={code} label={code} size="small" variant="outlined" />
                    ))}
                    {group.codes.length > 30 && (
                      <Chip label={`+${group.codes.length - 30}`} size="small" />
                    )}
                  </Box>
                  <Button
                    size="small"
                    onClick={() => toggleExpand(group)}
                    endIcon={expandedGroupId === group.id ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                    sx={{ mt: 1, textTransform: 'none' }}
                  >
                    {expandedGroupId === group.id ? 'Ocultar registros vinculados' : 'Conferir registros vinculados'}
                  </Button>
                  <Collapse in={expandedGroupId === group.id} timeout="auto" unmountOnExit>
                    {detailsById[group.id]?.loading ? (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 1 }}>
                        <CircularProgress size={16} />
                        <Typography variant="caption" color="text.secondary">Carregando…</Typography>
                      </Box>
                    ) : detailsById[group.id]?.error ? (
                      <Typography variant="caption" color="error" sx={{ py: 1, display: 'block' }}>
                        Não foi possível carregar os registros.
                      </Typography>
                    ) : (
                      <List dense disablePadding sx={{ mt: 0.5 }}>
                        {group.codes.map((code) => {
                          const companyName = detailsById[group.id]?.companies?.[String(code)];
                          const linked = Boolean(companyName);
                          return (
                            <ListItem key={code} disableGutters sx={{ py: 0.25 }}>
                              {linked ? (
                                <CheckCircleOutlineIcon sx={{ fontSize: 16, color: 'success.main', mr: 1, flexShrink: 0 }} />
                              ) : (
                                <ErrorOutlineIcon sx={{ fontSize: 16, color: 'warning.main', mr: 1, flexShrink: 0 }} />
                              )}
                              <ListItemText
                                primary={
                                  <Typography variant="body2" noWrap>
                                    <b>{code}</b>{' — '}
                                    {linked ? companyName : (
                                      <Typography component="span" variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                                        empresa não vinculada
                                      </Typography>
                                    )}
                                  </Typography>
                                }
                              />
                            </ListItem>
                          );
                        })}
                      </List>
                    )}
                  </Collapse>
                </>
              )}
            </Paper>
          ))}
        </List>
      )}

      <Dialog open={dialogOpen} onClose={closeDialog} fullWidth maxWidth="sm">
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          {form.id ? 'Editar grupo' : 'Novo grupo de clientes'}
          <IconButton onClick={closeDialog} size="small" disabled={saving}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {saveError && <Alert severity="error" sx={{ mb: 2 }}>{saveError}</Alert>}
          <TextField
            label="Nome do grupo"
            placeholder="Ex.: Lojas Online"
            fullWidth
            value={form.name}
            onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
            sx={{ mb: 2 }}
            autoFocus
          />
          <TextField
            label="Descrição (opcional)"
            fullWidth
            multiline
            minRows={2}
            value={form.description}
            onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))}
            sx={{ mb: 2 }}
          />
          <TextField
            label="Registros (códigos)"
            placeholder="Cole ou digite os códigos e pressione Enter"
            fullWidth
            value={codeInput}
            onChange={(e) => setCodeInput(e.target.value)}
            onKeyDown={handleCodeKeyDown}
            onBlur={addCodesFromInput}
            helperText="Separe por vírgula, espaço ou quebra de linha. Empresas com esses registros entram no grupo automaticamente."
          />
          {dialogCodes.length > 0 && (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 1.5 }}>
              {dialogCodes.map((code) => (
                <Chip key={code} label={code} size="small" onDelete={() => removeCode(code)} />
              ))}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialog} disabled={saving}>Cancelar</Button>
          <Button variant="contained" onClick={handleSave} disabled={saving}>
            {saving ? 'Salvando...' : 'Salvar'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ClientGroupsPage;
